import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import {
  AdminListExtractedDataBody,
  AdminListExtractedDataResponse,
  DerivedDataInfo,
  ExtractedDataInfo,
} from "@services/admin/types";
import { GptRequestsList } from "./components/GptRequestsList";
import { LinkedMessagesList } from "./components/LinkedMessagesList";

// Separate memoized component for JSON field to prevent re-renders
const JsonDataField = memo(
  ({
    value,
    onChange,
    error,
    onValidate,
  }: {
    value: string;
    onChange: (value: string) => void;
    error: string | null;
    onValidate: (value: string) => void;
  }) => {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          JSON Data *
        </Typography>
        <TextField
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            onValidate(e.target.value);
          }}
          multiline
          rows={10}
          fullWidth
          variant="outlined"
          error={!!error}
          helperText={error}
          sx={{ fontFamily: "monospace" }}
        />
      </Box>
    );
  }
);

interface OrganizationInfo {
  id: string;
  name: string;
}

export function AdminExtractedDataPage() {
  const [extractedData, setExtractedData] = useState<ExtractedDataInfo[]>([]);
  const [derivedData, setDerivedData] = useState<DerivedDataInfo[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [derivedTotal, setDerivedTotal] = useState(0);

  // Filter states
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [derivedDataIdFilter, setDerivedDataIdFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [idFilter, setIdFilter] = useState<string>("");
  const [tagFilterInput, setTagFilterInput] = useState<string>("");

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewJsonDialogOpen, setViewJsonDialogOpen] = useState(false);
  const [viewTextDialogOpen, setViewTextDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form states - separate JSON data to prevent re-renders
  const [jsonData, setJsonData] = useState("{}");
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    description: "",
    orgId: null as string | null,
    userId: null as string | null,
    dataText: "",
    tags: [] as string[],
  });

  // Track which fields have been changed for partial updates
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<ExtractedDataInfo | null>(
    null
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Load initial filters from URL params
    const initialId = searchParams.get("id");
    const initialDerivedDataId = searchParams.get("derived_data_id");

    if (initialDerivedDataId) {
      setDerivedDataIdFilter(initialDerivedDataId);
      // Clear any ID filter to avoid conflicts
      setIdFilter("");
      // Switch to extracted data tab when filtering by derived data
      setCurrentTab(0);
    } else if (initialId) {
      setIdFilter(initialId);
      // Clear derived data filter when using ID filter
      setDerivedDataIdFilter("");
    }

    // Load initial tab from URL params
    const initialTab = searchParams.get("tab");
    if (initialTab === "derived-data") {
      setCurrentTab(1);
    } else if (!initialDerivedDataId) {
      // Only set to 0 if no derived data filter is present
      setCurrentTab(0);
    }

    fetchOrganizations();
    setHasInitialized(true);
  }, [searchParams]); // Only reload on URL changes, not filter changes

  // Fetch data when filters change (including idFilter) - but only after initialization
  useEffect(() => {
    if (hasInitialized) {
      if (currentTab === 0) {
        fetchExtractedData();
      } else if (currentTab === 1) {
        fetchDerivedData();
      }
    }
  }, [
    idFilter,
    selectedOrgId,
    derivedDataIdFilter,
    typeFilter,
    tagsFilter,
    hasInitialized,
    currentTab,
  ]); // Fetch when any filter changes

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient.post<{ organizations: OrganizationInfo[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchExtractedData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (idFilter) filters.id = idFilter; // Add ID filter for server-side filtering
      if (selectedOrgId) filters.orgId = selectedOrgId;
      if (derivedDataIdFilter) filters.derivedDataId = derivedDataIdFilter; // Add derived data filter
      if (typeFilter) filters.type = typeFilter;
      if (tagsFilter.length > 0) filters.tags = tagsFilter;

      const res = await apiClient.post<
        AdminListExtractedDataResponse,
        AdminListExtractedDataBody
      >(`/admin/extracted-data/list`, { limit: 100, offset: 0, ...filters });

      // Data is already filtered server-side
      setExtractedData(res.data as any);
      setTotal(res.total);
    } catch (error) {
      console.error("Error fetching extracted data:", error);
      setError("Failed to load extracted data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDerivedData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (idFilter) filters.id = idFilter; // Add ID filter for server-side filtering
      if (derivedDataIdFilter) filters.id = derivedDataIdFilter; // Add ID filter for server-side filtering
      if (selectedOrgId) filters.orgId = selectedOrgId;
      if (typeFilter) filters.type = typeFilter;
      if (tagsFilter.length > 0) filters.tags = tagsFilter;

      const res = await apiClient.post<{
        data: DerivedDataInfo[];
        total: number;
      }>(`/admin/derived-data/list`, { limit: 100, offset: 0, ...filters });

      // Data is already filtered server-side
      setDerivedData(res.data);
      setDerivedTotal(res.total);
    } catch (error) {
      console.error("Error fetching derived data:", error);
      setError("Failed to load derived data");
    } finally {
      setLoading(false);
    }
  };

  const validateJson = useCallback((jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      setJsonError(null);
      return true;
    } catch (e) {
      setJsonError("Invalid JSON format");
      return false;
    }
  }, []);

  // Debounced JSON validation - only validate after user stops typing
  const debouncedValidateJson = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return {
      validate: (jsonString: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          validateJson(jsonString);
        }, 300); // Wait 300ms after user stops typing
      },
      cleanup: () => clearTimeout(timeoutId),
    };
  }, [validateJson]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => debouncedValidateJson.cleanup();
  }, [debouncedValidateJson]);

  const handleCreateData = async () => {
    if (!validateJson(jsonData)) return;

    try {
      setError(null);
      const res = await apiClient.post<{ data: ExtractedDataInfo }>(
        `/admin/extracted-data/create`,
        {
          ...formData,
          data: JSON.parse(jsonData),
          dataText: formData.dataText || null,
          userId: formData.userId || null,
        }
      );
      setExtractedData([res.data, ...extractedData]);
      setCreateDialogOpen(false);
      resetForm();
    } catch (error: any) {
      console.error("Error creating extracted data:", error);
      setError(error.message || "Failed to create extracted data");
    }
  };

  const handleUpdateData = async () => {
    if (!selectedItem || !validateJson(jsonData)) return;

    try {
      setError(null);

      // Build payload with only changed fields to avoid large payloads
      const updatePayload: any = { id: selectedItem.id };

      // Only include fields that have been explicitly changed
      if (changedFields.has("type")) {
        updatePayload.type = formData.type;
      }
      if (changedFields.has("name")) {
        updatePayload.name = formData.name;
      }
      if (changedFields.has("description")) {
        updatePayload.description = formData.description;
      }
      if (changedFields.has("orgId")) {
        updatePayload.orgId = formData.orgId;
      }
      if (changedFields.has("userId")) {
        updatePayload.userId = formData.userId || null;
      }
      if (changedFields.has("data")) {
        updatePayload.data = JSON.parse(jsonData);
      }
      if (changedFields.has("dataText")) {
        updatePayload.dataText = formData.dataText || null;
      }
      if (changedFields.has("tags")) {
        updatePayload.tags = formData.tags;
      }

      // If no fields were changed, don't make the request
      if (Object.keys(updatePayload).length === 1) {
        setEditDialogOpen(false);
        resetForm();
        return;
      }

      const res = await apiClient.put<{ data: ExtractedDataInfo }>(
        `/admin/extracted-data/update`,
        updatePayload
      );
      setExtractedData(
        extractedData.map((item) =>
          item.id === selectedItem.id ? res.data : item
        )
      );
      setEditDialogOpen(false);
      resetForm();
    } catch (error: any) {
      console.error("Error updating extracted data:", error);
      setError(error.message || "Failed to update extracted data");
    }
  };

  const handleDeleteData = async () => {
    if (!selectedItem) return;

    try {
      setError(null);
      await apiClient.delete(`/admin/extracted-data/delete`, {
        data: { id: selectedItem.id },
      });
      setExtractedData(
        extractedData.filter((item) => item.id !== selectedItem.id)
      );
      setDeleteConfirmOpen(false);
      setSelectedItem(null);
    } catch (error: any) {
      console.error("Error deleting extracted data:", error);
      setError(error.message || "Failed to delete extracted data");
    }
  };

  const openEditDialog = (item: ExtractedDataInfo) => {
    setSelectedItem(item);
    setFormData({
      type: item.type,
      name: item.name || "",
      description: item.description || "",
      orgId: item.orgId,
      userId: item.userId,
      dataText: item.dataText || "",
      tags: item.tags,
    });
    setJsonData(JSON.stringify(item.data, null, 2));
    setChangedFields(new Set()); // Reset changed fields
    setJsonError(null); // Clear any JSON errors
    debouncedValidateJson.cleanup(); // Clear any pending validation
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (item: ExtractedDataInfo) => {
    setSelectedItem(item);
    setDeleteConfirmOpen(true);
  };

  const viewJsonData = (item: ExtractedDataInfo) => {
    setSelectedItem(item);
    setViewJsonDialogOpen(true);
  };

  const viewTextData = (item: ExtractedDataInfo) => {
    setSelectedItem(item);
    setViewTextDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      type: "",
      name: "",
      description: "",
      orgId: null,
      userId: null,
      dataText: "",
      tags: [],
    });
    setJsonData("{}");
    setJsonError(null);
    setTagInput("");
    setChangedFields(new Set());
    setSelectedItem(null);
    debouncedValidateJson.cleanup(); // Clear any pending validation
  };

  // Helper function to update form data and track changes
  const updateFormField = useCallback((field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setChangedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(field);
      return newSet;
    });
  }, []);

  // Stable callback for JSON field to prevent re-renders
  const handleJsonChange = useCallback((value: string) => {
    setJsonData(value);
    setChangedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add("data");
      return newSet;
    });
  }, []);

  // Stable callback for JSON validation
  const handleJsonValidation = useCallback(
    (value: string) => {
      debouncedValidateJson.validate(value);
    },
    [debouncedValidateJson]
  );

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateFormField("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    updateFormField(
      "tags",
      formData.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleSearch = () => {
    if (currentTab === 0) {
      fetchExtractedData();
    } else if (currentTab === 1) {
      fetchDerivedData();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const addTagFilter = () => {
    if (tagFilterInput.trim() && !tagsFilter.includes(tagFilterInput.trim())) {
      setTagsFilter([...tagsFilter, tagFilterInput.trim()]);
      setTagFilterInput("");
    }
  };

  const removeTagFilter = (tagToRemove: string) => {
    setTagsFilter(tagsFilter.filter((tag) => tag !== tagToRemove));
  };

  const clearFilters = () => {
    setSelectedOrgId("");
    setTypeFilter("");
    setTagsFilter([]);
    setIdFilter("");
    setTagFilterInput("");
    // Trigger search with cleared filters
    setTimeout(() => fetchExtractedData(), 0);
  };

  const truncateText = (text: string, maxLength: number = 20) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <AdminPage>
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
        >
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Data Management
          </Typography>
          {currentTab === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Extracted Data
            </Button>
          )}
        </Box>

        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => {
              setCurrentTab(newValue);
              // Update URL params
              const newSearchParams = new URLSearchParams(searchParams);
              if (newValue === 1) {
                newSearchParams.set("tab", "derived-data");
              } else {
                newSearchParams.delete("tab");
              }
              navigate(`/admin/extracted-data?${newSearchParams.toString()}`, {
                replace: true,
              });
            }}
          >
            <Tab label="Extracted Data" />
            <Tab label="Derived Data" />
          </Tabs>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ marginBottom: "16px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Tab Content */}
        {currentTab === 0 && (
          <>
            {/* Extracted Data Content */}
            {/* Filters */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Organization</InputLabel>
                      <Select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                        label="Organization"
                      >
                        <MenuItem value="">All Data</MenuItem>
                        <MenuItem value="null">No Organization</MenuItem>
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Type Filter"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{ minWidth: 140 }}
                      placeholder="e.g. shipment"
                    />

                    <TextField
                      size="small"
                      label="Add Tag Filter"
                      value={tagFilterInput}
                      onChange={(e) => setTagFilterInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTagFilter();
                        }
                      }}
                      sx={{ minWidth: 140 }}
                      placeholder="Enter tag name"
                    />

                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      size="small"
                    >
                      Search
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      size="small"
                    >
                      Clear
                    </Button>

                    {idFilter && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setIdFilter("");
                          navigate("/admin/extracted-data", { replace: true });
                        }}
                      >
                        Clear ID Filter
                      </Button>
                    )}

                    <Typography variant="body2" color="text.secondary">
                      Total: {total} records
                    </Typography>
                  </Stack>

                  {/* Active Tag Filters */}
                  {tagsFilter.length > 0 && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Active tag filters:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {tagsFilter.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            onDelete={() => removeTagFilter(tag)}
                            size="small"
                            color="primary"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {idFilter && (
                    <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                      Showing data for ID: <strong>{idFilter}</strong>
                    </Alert>
                  )}

                  {derivedDataIdFilter && (
                    <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                      Showing extracted data related to derived data:{" "}
                      <strong>{derivedDataIdFilter}</strong>
                      <Button
                        size="small"
                        sx={{ ml: 2 }}
                        onClick={() => {
                          const url = new URL(window.location.href);
                          url.searchParams.delete("derived_data_id");
                          url.searchParams.delete("id");
                          window.location.href = url.toString();
                        }}
                      >
                        Clear Filter
                      </Button>
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Organization</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Tags</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Data</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Text</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Updated</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extractedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No extracted data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    extractedData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.description || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.orgId ? (
                            organizations.find((org) => org.id === item.orgId)
                              ?.name || item.orgId
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {item.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Click to view full JSON">
                            <Button
                              size="small"
                              onClick={() => viewJsonData(item)}
                              startIcon={<VisibilityIcon />}
                            >
                              {truncateText(JSON.stringify(item.data))}
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                        >
                          {item.dataText ? (
                            <Tooltip title="Click to view full text">
                              <Button
                                size="small"
                                onClick={() => viewTextData(item)}
                                startIcon={<VisibilityIcon />}
                                sx={{ whiteSpace: "normal", textAlign: "left" }}
                              >
                                {truncateText(item.dataText)}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(item.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(item.updatedAt).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            {item.gptRequestIds &&
                              item.gptRequestIds.length > 0 && (
                                <GptRequestsList
                                  gptRequestIds={item.gptRequestIds}
                                  size="small"
                                />
                              )}
                            {item.emails && item.emails.length > 0 && (
                              <LinkedMessagesList
                                emails={item.emails}
                                size="small"
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(item)}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(item)}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {currentTab === 1 && (
          <>
            {/* Derived Data Content */}
            {/* Filters - Same as ExtractedData */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Organization</InputLabel>
                      <Select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                        label="Organization"
                      >
                        <MenuItem value="">All Data</MenuItem>
                        <MenuItem value="null">No Organization</MenuItem>
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Type Filter"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{ minWidth: 140 }}
                      placeholder="e.g. processed"
                    />

                    <TextField
                      size="small"
                      label="Add Tag Filter"
                      value={tagFilterInput}
                      onChange={(e) => setTagFilterInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTagFilter();
                        }
                      }}
                      sx={{ minWidth: 140 }}
                      placeholder="Enter tag name"
                    />

                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      size="small"
                    >
                      Search
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      size="small"
                    >
                      Clear
                    </Button>

                    {idFilter && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setIdFilter("");
                          navigate("/admin/extracted-data?tab=derived-data", {
                            replace: true,
                          });
                        }}
                      >
                        Clear ID Filter
                      </Button>
                    )}

                    <Typography variant="body2" color="text.secondary">
                      Total: {derivedTotal} records
                    </Typography>
                  </Stack>

                  {/* Active Tag Filters */}
                  {tagsFilter.length > 0 && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Active tag filters:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {tagsFilter.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            onDelete={() => removeTagFilter(tag)}
                            size="small"
                            color="primary"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {idFilter && (
                    <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                      Showing derived data for ID: <strong>{idFilter}</strong>
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* DerivedData Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Organization</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Tags</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Data</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Text</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Updated</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {derivedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No derived data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    derivedData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.orgId ? (
                            organizations.find((org) => org.id === item.orgId)
                              ?.name || item.orgId
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {item.tags.map((tag: string) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Click to view full JSON">
                            <Button
                              size="small"
                              onClick={() => viewJsonData(item as any)}
                              startIcon={<VisibilityIcon />}
                            >
                              {truncateText(JSON.stringify(item.data))}
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                        >
                          {item.dataText ? (
                            <Tooltip title="Click to view full text">
                              <Button
                                size="small"
                                onClick={() => viewTextData(item as any)}
                                startIcon={<VisibilityIcon />}
                                sx={{ whiteSpace: "normal", textAlign: "left" }}
                              >
                                {truncateText(item.dataText)}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(item.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(item.updatedAt).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                const url = new URL(window.location.href);
                                url.searchParams.set(
                                  "derived_data_id",
                                  item.id
                                );
                                url.searchParams.delete("tab");
                                url.searchParams.delete("id"); // Remove ID filter
                                window.location.href = url.toString();
                              }}
                              title="View Related Extracted Data"
                            >
                              <LinkIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => viewJsonData(item as any)}
                              title="View JSON Data"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={createDialogOpen || editDialogOpen}
          onClose={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {createDialogOpen
              ? "Create New Extracted Data"
              : "Edit Extracted Data"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Type"
                value={formData.type}
                onChange={(e) => updateFormField("type", e.target.value)}
                fullWidth
                required
                helperText="Will be automatically lowercased"
              />

              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => updateFormField("name", e.target.value)}
                fullWidth
                helperText="Human-readable name for this data"
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => updateFormField("description", e.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText="Description of what this data contains"
              />

              <FormControl fullWidth required>
                <InputLabel>Organization *</InputLabel>
                <Select
                  value={formData.orgId || ""}
                  onChange={(e) =>
                    updateFormField("orgId", e.target.value || null)
                  }
                  label="Organization *"
                >
                  <MenuItem value="" disabled>
                    Select Organization
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="User ID (Optional)"
                value={formData.userId || ""}
                onChange={(e) =>
                  updateFormField("userId", e.target.value || null)
                }
                fullWidth
              />

              <JsonDataField
                value={jsonData}
                onChange={handleJsonChange}
                error={jsonError}
                onValidate={handleJsonValidation}
              />

              <TextField
                label="Text Data (Optional)"
                value={formData.dataText}
                onChange={(e) => updateFormField("dataText", e.target.value)}
                multiline
                rows={3}
                fullWidth
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <TextField
                    size="small"
                    placeholder="Add tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <Button onClick={addTag} size="small">
                    Add
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => removeTag(tag)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>

              {editDialogOpen && selectedItem && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(selectedItem.createdAt).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Updated: {new Date(selectedItem.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={createDialogOpen ? handleCreateData : handleUpdateData}
              variant="contained"
              disabled={!formData.type || !formData.orgId || !!jsonError}
            >
              {createDialogOpen ? "Create" : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View JSON Dialog */}
        <Dialog
          open={viewJsonDialogOpen}
          onClose={() => setViewJsonDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography>JSON Data</Typography>
              <IconButton
                onClick={() =>
                  copyToClipboard(
                    JSON.stringify(selectedItem?.data, null, 2) || ""
                  )
                }
                title="Copy to clipboard"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
              <pre style={{ margin: 0, overflow: "auto" }}>
                {JSON.stringify(selectedItem?.data, null, 2)}
              </pre>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewJsonDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* View Text Dialog */}
        <Dialog
          open={viewTextDialogOpen}
          onClose={() => setViewTextDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography>Text Data</Typography>
              <IconButton
                onClick={() => copyToClipboard(selectedItem?.dataText || "")}
                title="Copy to clipboard"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
              <Typography
                component="pre"
                sx={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: "14px",
                }}
              >
                {selectedItem?.dataText || "No text data available"}
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewTextDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this extracted data entry of type
              "{selectedItem?.type}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteData}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
